import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Trash2, AlertCircle, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageHeaderSkeleton, AccountCardSkeleton } from '@/components/LoadingSkeletons';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { MutualFundMetricsCard } from '@/components/mutual-fund-metrics-card/MutualFundMetricsCard';
import { NavChart } from '@/components/nav-chart/NavChart';
import { TimelineToggle } from '@/components/nav-chart/TimelineToggle';
import { MutualFundForm } from '@/components/mutual-fund-form/MutualFundForm';
import { MutualFundDividendForm } from '@/components/mutual-fund-dividend-form/MutualFundDividendForm';
import { MutualFundDividendTable } from '@/components/mutual-fund-dividend-table/MutualFundDividendTable';
import {
  useMutualFunds,
  useMutualFundNavHistory,
  useUpdateMutualFund,
  useDeleteMutualFund,
} from '@/hooks/useMutualFunds';
import {
  useMutualFundDividends,
  useCreateMutualFundDividend,
  computeTotalDividends,
} from '@/hooks/useMutualFundDividends';
import { useFetchAccounts } from '@accountHooks';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import type { NavPeriod } from '@/components/nav-chart/NavChart';
import type { MutualFundFormData } from '@/types/mutualFund';
import type { MutualFundDividendFormData } from '@/types/mutualFundDividend';

const NAV_PERIODS: NavPeriod[] = ['YTD', '1M', '3M', '6M', '1Y', '3Y', '5Y', 'ALL'];

export default function MutualFundAccountDetail() {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();

  // ── Page state ────────────────────────────────────────────────────────────
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<NavPeriod>('1Y');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dividendDialogOpen, setDividendDialogOpen] = useState(false);
  const [dividendPage, setDividendPage] = useState(1);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: funds = [], isLoading: fundsLoading } = useMutualFunds();
  const fund = funds.find((f) => f.id === fundId) ?? null;

  const { data: navHistory = [], isLoading: navLoading } = useMutualFundNavHistory(
    fund?.id ?? null
  );

  // ── Dividend data ─────────────────────────────────────────────────────────
  const {
    data: dividendsData,
    isLoading: dividendsLoading,
    isError: dividendsError,
    refetch: refetchDividends,
  } = useMutualFundDividends(fund?.id ?? '', dividendPage);

  const dividends = dividendsData?.dividends ?? [];
  const dividendsTotalCount = dividendsData?.totalCount ?? 0;

  const createDividend = useCreateMutualFundDividend();

  const {
    data: accounts = [],
    isLoading: accountsLoading,
    isError: accountsError,
  } = useFetchAccounts();

  // ── Currency conversion ───────────────────────────────────────────────────
  const baseCurrency = fund?.currency ?? 'USD';
  const displayCurrency = selectedCurrency ?? baseCurrency;

  const {
    convert,
    isLoading: ratesLoading,
    error: ratesError,
  } = useExchangeRates(baseCurrency);

  // Show toast once when conversion fails
  const [ratesErrorToasted, setRatesErrorToasted] = useState(false);
  if (ratesError && !ratesErrorToasted) {
    setRatesErrorToasted(true);
    toast.error('Currency conversion unavailable. Showing values in base currency.');
  }

  const convertValue = (amount: number): number => {
    if (ratesLoading || ratesError || displayCurrency === baseCurrency) return amount;
    try {
      return convert(amount, baseCurrency, displayCurrency);
    } catch {
      return amount;
    }
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const updateFund = useUpdateMutualFund();
  const deleteFund = useDeleteMutualFund();

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleBack = () => navigate('/investments/mutual-funds');

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    setRatesErrorToasted(false);
  };

  const handleEditSave = async (data: MutualFundFormData) => {
    if (!fund) return;
    try {
      await updateFund.mutateAsync({ id: fund.id, ...data });
      toast.success('Mutual fund updated successfully');
      setEditDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update mutual fund');
      // Keep dialog open on failure
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fund) return;
    try {
      await deleteFund.mutateAsync(fund.id);
      toast.success('Mutual fund deleted successfully');
      setDeleteDialogOpen(false);
      navigate('/investments/mutual-funds');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete mutual fund');
      setDeleteDialogOpen(false);
    }
  };

  const handleDividendSave = (data: MutualFundDividendFormData) => {
    if (!fund) return;
    createDividend.mutate(
      { formData: data },
      {
        onSuccess: () => {
          setDividendDialogOpen(false);
        },
      }
    );
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (fundsLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite">
        <PageHeaderSkeleton />
        <AccountCardSkeleton />
        <AccountCardSkeleton />
      </div>
    );
  }

  // ── Not found state ───────────────────────────────────────────────────────
  if (!fund) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Fund not found.{' '}
            <button
              onClick={handleBack}
              className="underline font-medium"
              aria-label="Go back to mutual funds list"
            >
              Back to Mutual Funds
            </button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const meta = fund.mutual_fund_metadata;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-2 self-start min-h-[44px]"
          aria-label="Back to Mutual Funds"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Mutual Funds
        </Button>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDividendDialogOpen(true)}
            className="min-h-[44px]"
            aria-label={`Record dividend for ${fund.fund_name}`}
          >
            <PlusCircle className="h-4 w-4 mr-2" aria-hidden="true" />
            Record Dividend
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditDialogOpen(true)}
            className="min-h-[44px]"
            aria-label={`Edit ${fund.fund_name}`}
          >
            <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
            Edit Account
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            className="min-h-[44px]"
            aria-label={`Delete ${fund.fund_name}`}
          >
            <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
            Delete Account
          </Button>
        </div>
      </div>

      {/* ── Fund summary card ── */}
      <Card>
        <CardHeader>
          <CardTitle>Fund Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold">{fund.fund_name}</h2>
            <p className="text-muted-foreground">{fund.fund_manager}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Account / Folio Number</p>
              <p className="font-mono text-sm font-medium">{fund.account_number}</p>
            </div>

            {meta && (
              <div>
                <p className="text-sm text-muted-foreground">Fund Category</p>
                <Badge variant="secondary" className="capitalize">
                  {meta.fund_category}
                </Badge>
              </div>
            )}

            {meta?.isin && (
              <div>
                <p className="text-sm text-muted-foreground">ISIN</p>
                <p className="font-mono text-sm font-medium">{meta.isin}</p>
              </div>
            )}

            {meta?.benchmark_index && (
              <div>
                <p className="text-sm text-muted-foreground">Benchmark Index</p>
                <p className="text-sm font-medium">{meta.benchmark_index}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Currency toggle ── */}
      <CurrencyToggle
        baseCurrency={baseCurrency}
        selectedCurrency={displayCurrency}
        onCurrencyChange={handleCurrencyChange}
        showBalance={false}
      />

      {/* ── Metrics card ── */}
      <MutualFundMetricsCard
        metadata={meta}
        currency={ratesLoading || ratesError ? baseCurrency : displayCurrency}
        baseCurrency={baseCurrency}
        convertValue={convertValue}
      />

      {/* ── NAV chart ── */}
      <div className="space-y-3">
        <TimelineToggle
          periods={NAV_PERIODS}
          selected={selectedPeriod}
          onChange={setSelectedPeriod}
        />
        <NavChart
          data={navHistory}
          period={selectedPeriod}
          currency={ratesLoading || ratesError ? baseCurrency : displayCurrency}
          baseCurrency={baseCurrency}
          convertValue={convertValue}
          isLoading={navLoading}
        />
      </div>

      {/* ── Total Dividends Paid metric tile ── */}
      {(() => {
        const totalDividendsBase = computeTotalDividends(dividends);
        const totalDividendsConverted = convertValue(totalDividendsBase);
        const dividendCurrency = ratesLoading || ratesError ? baseCurrency : displayCurrency;
        const showFallbackNotice =
          ratesError && displayCurrency !== baseCurrency;

        return (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Dividends Paid
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalDividendsConverted.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                <span className="text-base font-normal text-muted-foreground">
                  {dividendCurrency}
                </span>
              </div>
              {showFallbackNotice && (
                <p className="text-xs text-muted-foreground mt-1">
                  Conversion unavailable — showing in {baseCurrency}.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* ── Dividend payment history ── */}
      <Card>
        <CardHeader>
          <CardTitle>Dividend Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          <MutualFundDividendTable
            dividends={dividends}
            isLoading={dividendsLoading}
            isError={dividendsError}
            onRetry={refetchDividends}
            page={dividendPage}
            onPageChange={setDividendPage}
            totalCount={dividendsTotalCount}
          />
        </CardContent>
      </Card>

      {/* ── Record Dividend dialog ── */}
      <Dialog open={dividendDialogOpen} onOpenChange={setDividendDialogOpen}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full"
          aria-describedby="dividend-dialog-description"
        >
          <DialogHeader>
            <DialogTitle>Record Dividend</DialogTitle>
            <DialogDescription id="dividend-dialog-description">
              Record a dividend payment received for {fund.fund_name}.
            </DialogDescription>
          </DialogHeader>
          <MutualFundDividendForm
            fund={fund}
            accounts={accounts}
            accountsLoading={accountsLoading}
            accountsError={accountsError}
            onSave={handleDividendSave}
            onCancel={() => setDividendDialogOpen(false)}
            isSubmitting={createDividend.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* ── Edit dialog ── */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-[640px] w-[95vw] sm:w-full"
          aria-describedby="edit-dialog-description"
        >
          <DialogHeader>
            <DialogTitle>Edit Mutual Fund</DialogTitle>
            <DialogDescription id="edit-dialog-description">
              Update the details of your mutual fund investment.
            </DialogDescription>
          </DialogHeader>
          <MutualFundForm
            mutualFund={fund}
            onSave={handleEditSave}
            onCancel={() => setEditDialogOpen(false)}
            isSubmitting={updateFund.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* ── Delete confirmation dialog ── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent
          className="w-[95vw] sm:w-full sm:max-w-[500px]"
          aria-describedby="delete-dialog-description"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mutual Fund</AlertDialogTitle>
            <AlertDialogDescription id="delete-dialog-description">
              This action cannot be undone. <strong>{fund.fund_name}</strong>, all NAV history,
              and associated transaction records will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel className="min-h-[44px] w-full sm:w-auto">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteFund.isPending}
              className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm deletion of ${fund.fund_name}`}
            >
              {deleteFund.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
